import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
} from 'react';
import api from '../services/api';

interface IUser {
  id: string;
  name: string;
  email: string;
  rights: 1 | 2 | 3 | 4;
  customer: {
    tradeName: string;
  };
  customer_id?: string;
  two_fa_enabled: boolean;
}

interface IAuthState {
  token: string;
  user: IUser;
}

interface ISignInCredentials {
  email: string;
  password: string;
}

type ISignInResult =
  | { requiresTwoFactor: true; email: string }
  | { requiresTwoFactor: false };

interface IAuthContext {
  user: IUser | null;
  loading: boolean;
  signIn(credentials: ISignInCredentials): Promise<ISignInResult>;
  verifyTwoFactor(data: { email: string; code: string }): Promise<void>;
  signOut(): void;
  updateUser(user: IUser): void;
}

const AuthContext = createContext<IAuthContext>({} as IAuthContext);

export const AuthProvider: React.FC = ({ children }) => {
  const [data, setData] = useState<IAuthState | null>(null);
  const [loading, setLoading] = useState(true);

  const signOut = useCallback(async () => {
    try {
      await api.delete('/sessions/');
    } catch {
      // ignora erro de rede no logout
    }
    api.defaults.headers.authorization = '';
    localStorage.removeItem('@AlutalApp:user');
    setData(null);
  }, []);

  useEffect(() => {
    async function recoverSession() {
      const storedUser = localStorage.getItem('@AlutalApp:user');

      try {
        const { data: refreshData } = await api.post<{ token: string }>(
          '/sessions/refresh',
        );

        const token = refreshData.token;
        api.defaults.headers.authorization = `Bearer ${token}`;

        if (storedUser) {
          setData({ token, user: JSON.parse(storedUser) });
        } else {
          await signOut();
        }
      } catch {
        localStorage.removeItem('@AlutalApp:user');
        setData(null);
      } finally {
        setLoading(false);
      }
    }

    recoverSession();
  }, [signOut]);

  useEffect(() => {
    const handleLogout = () => signOut();
    const handleTokenRefreshed = (e: Event) => {
      const newToken = (e as CustomEvent<string>).detail;
      setData(prev => (prev ? { ...prev, token: newToken } : null));
    };

    window.addEventListener('auth:logout', handleLogout);
    window.addEventListener('auth:tokenRefreshed', handleTokenRefreshed);

    return () => {
      window.removeEventListener('auth:logout', handleLogout);
      window.removeEventListener('auth:tokenRefreshed', handleTokenRefreshed);
    };
  }, [signOut]);

  const signIn = useCallback(
    async ({ email, password }: ISignInCredentials): Promise<ISignInResult> => {
      const response = await api.post<
        | { requiresTwoFactor: true; email: string }
        | { user: IUser; token: string }
      >('/sessions/', { email, password });

      if ('requiresTwoFactor' in response.data) {
        return { requiresTwoFactor: true, email: response.data.email };
      }

      const { token, user } = response.data as { user: IUser; token: string };
      api.defaults.headers.authorization = `Bearer ${token}`;
      localStorage.setItem('@AlutalApp:user', JSON.stringify(user));
      setData({ token, user });

      return { requiresTwoFactor: false };
    },
    [],
  );

  const verifyTwoFactor = useCallback(
    async ({ email, code }: { email: string; code: string }): Promise<void> => {
      const response = await api.post<{ token: string; user: IUser }>(
        '/sessions/verify-2fa',
        { email, code },
      );
      const { token, user } = response.data;
      api.defaults.headers.authorization = `Bearer ${token}`;
      localStorage.setItem('@AlutalApp:user', JSON.stringify(user));
      setData({ token, user });
    },
    [],
  );

  const updateUser = useCallback((user: IUser) => {
    setData(prev => (prev ? { ...prev, user } : null));
    localStorage.setItem('@AlutalApp:user', JSON.stringify(user));
  }, []);

  return (
    <AuthContext.Provider
      value={{ user: data?.user ?? null, loading, signIn, verifyTwoFactor, signOut, updateUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): IAuthContext {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
